import { memo, useCallback } from 'react'

import { Box, IconButton, SvgIcon, Tooltip, Typography } from '@mui/material'
import { AssetsGetRequest } from '@nativewaves/nw-cloud-sdk/media'
import { useNavigate } from '@tanstack/react-router'
import { ArrowDownCircleFill } from 'emotion-icons/bootstrap'
import { omit, omitBy, pickBy } from 'lodash-es'
import { motion } from 'motion/react'
import { useTranslation } from 'react-i18next'

import { mediaPlatformAssetsOverviewRoute } from 'pages/NWPlatform/MediaPlatforms/MediaPlatform/Assets'

type SortableHeaderProps = {
  title: string
  sortProp: keyof PickStartsWith<AssetsGetRequest, 'orderBy'>
}

const SortableHeaderRaw = ({ title, sortProp }: SortableHeaderProps) => {
  const { t } = useTranslation(['common', 'entity', 'domain'])

  const navigate = useNavigate({ from: mediaPlatformAssetsOverviewRoute.fullPath })
  const search = mediaPlatformAssetsOverviewRoute.useSearch({
    select: (items) =>
      pickBy(items, (_, key) => key.startsWith('orderBy')) as PickStartsWith<AssetsGetRequest, 'orderBy'>,
    structuralSharing: true,
  })

  const handleSorting = useCallback(
    (sortProperty: keyof PickStartsWith<AssetsGetRequest, 'orderBy'>) => {
      if (sortProperty in search) {
        if (search[sortProperty] === 'Desc') {
          navigate({ to: mediaPlatformAssetsOverviewRoute.to, search: (current) => omit(current, sortProperty) })
        } else {
          navigate({
            to: mediaPlatformAssetsOverviewRoute.to,
            search: (current) => ({ ...current, [sortProperty]: 'Desc' }),
          })
        }
      } else {
        navigate({
          to: mediaPlatformAssetsOverviewRoute.to,
          search: (current) => ({ ...omitBy(current, (_, key) => key.startsWith('orderBy')), [sortProperty]: 'Asc' }),
        })
      }
    },
    [navigate, search],
  )

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1 }}>
      <Typography variant="inherit">{title}</Typography>
      <Tooltip
        title={
          !(sortProp in search)
            ? t('sortAscending')
            : search[sortProp] === 'Asc'
              ? t('sortDescending')
              : t('sortDisable')
        }
      >
        <IconButton size="small" onClick={() => handleSorting(sortProp)}>
          <SvgIcon
            fontSize="inherit"
            color={search[sortProp] ? 'primary' : 'inherit'}
            component={motion.svg}
            animate={search[sortProp] === 'Desc' ? { rotateZ: '180deg' } : { rotateZ: '0deg' }}
          >
            <ArrowDownCircleFill />
          </SvgIcon>
        </IconButton>
      </Tooltip>
    </Box>
  )
}

export const SortableHeader = memo(SortableHeaderRaw)
